import { useState } from "react";
import type { ViewStyle } from "react-native";
import { Box } from "../../ui/box";
import { HStack } from "../../ui/hstack";
import { Image } from "../../ui/image";
import { Text } from "../../ui/text";

/** How many cards the filmstrip shows at most — the backend already caps
 * `thumbnails` at 6, this is just the ceiling for the placeholder strip too. */
const MAX_CARDS = 6;
const PLACEHOLDER_CARDS = 3;
const TOP_COUNT = 3;

/** Alternating tiny tilts (±2–3°), cycled by index rather than random, so the
 * filmstrip reads as a loosely fanned handful of real ads rather than a
 * machine-straight grid — but stays stable across re-renders. */
const ROTATIONS = [
  "rotate-[-3deg]",
  "rotate-[2deg]",
  "rotate-[-2deg]",
  "rotate-[3deg]",
  "rotate-[-2deg]",
  "rotate-[2deg]",
];

/** `animationDelay` is web-runtime-only — RN's `ViewStyle` type has no
 * vocabulary for it, same carve-out `ProductChips`' own delay helper takes.
 * 70ms per card is what makes the strip read as ads landing one after another
 * rather than all at once. */
const delayStyle = (index: number): ViewStyle =>
  ({ animationDelay: `${index * 70}ms` }) as ViewStyle;

/**
 * Hard pixel size, not `aspect-[9/16]` off a lone `h-48` — the aspect-ratio-only
 * version was the bug: with only one axis constrained, a flex row of shrink-0
 * cards would occasionally settle on the image's own tiny intrinsic size instead
 * of the intended portrait card, rendering as a ~40px thumbnail rather than a
 * real ad-sized one. `min-h-48 min-w-32` on top of the fixed size is a second
 * guard against ever shrinking below it.
 */
const CARD_CLASS =
  "h-48 w-32 min-h-48 min-w-32 shrink-0 overflow-hidden rounded-xl border-2 bg-background-100 animate-fade-rise motion-reduce:animate-none";

/**
 * One ad in the filmstrip.
 *
 * Fails gracefully: a dead URL (a scrape that returned a since-removed post, a
 * CDN hiccup) hides its own card instead of showing a broken-image icon — the
 * evidence is meant to build confidence, not visibly break. Top 3 get a warm
 * ring and a slight lift only once the deck has settled (the job completed):
 * thumbnails now stream in mid-scrape from previous runs, and highlighting a
 * provisional set would promise a "top 3" the final results may not keep.
 */
const ThumbnailCard = ({
  url,
  index,
  highlighted,
}: {
  url: string;
  index: number;
  highlighted: boolean;
}) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <Box
      className={`${CARD_CLASS} ${index === 0 ? "" : "-ml-6"} ${ROTATIONS[index % ROTATIONS.length]} ${
        highlighted
          ? "-translate-y-2 border-warning-400 shadow-soft-2"
          : "border-background-0 shadow-soft-1"
      }`}
      style={delayStyle(index)}
    >
      <Image
        source={{ uri: url }}
        size="full"
        alt="Contenido ganador analizado"
        contentFit="cover"
        onError={() => setFailed(true)}
      />
    </Box>
  );
};

/** A card with no real image yet — the filmstrip shape is already legible
 * while the scrape is still running, before there is anything to show inside it. */
const PlaceholderCard = ({ index }: { index: number }) => (
  <Box
    className={`${CARD_CLASS} border-background-0 ${index === 0 ? "" : "-ml-6"} ${ROTATIONS[index % ROTATIONS.length]}`}
    style={delayStyle(index)}
  >
    <Box className="h-full w-full animate-shimmer motion-reduce:animate-none" />
  </Box>
);

type Props = {
  /** Thumbnails to fan out, in order — evidence from previous scrapes while the
   * job runs, replaced by the definitive winner set once it completes. */
  thumbnails: string[];
  /** Show the fanning-in placeholder strip instead — there is nothing to show yet,
   * but the shape of what is coming. */
  placeholder: boolean;
  /** True once the research job completed: turns on the top-3 highlight ring,
   * which only makes sense for the definitive set. */
  settled: boolean;
};

/**
 * The big side-by-side filmstrip of winning ads/reels the research job is
 * studying — moment 2's evidence, folded into `AnimationSlot`'s single card
 * rather than a card of its own. Portrait, real-ad-sized (`w-32 h-48`), because
 * a thumbnail this size is what makes the deck read as "the AI is literally
 * looking at these ads" rather than as decoration.
 */
const ThumbnailDeck = ({ thumbnails, placeholder, settled }: Props) => {
  const hasThumbnails = thumbnails.length > 0;
  if (!hasThumbnails && !placeholder) return null;

  const cards = hasThumbnails ? thumbnails.slice(0, MAX_CARDS) : [];

  return (
    <Box className="mt-4">
      <HStack className="items-end justify-center overflow-visible pl-2 pt-2">
        {hasThumbnails
          ? cards.map((url, index) => (
              <ThumbnailCard
                key={`${url}-${index}`}
                url={url}
                index={index}
                highlighted={settled && index < TOP_COUNT}
              />
            ))
          : Array.from({ length: PLACEHOLDER_CARDS }, (_, index) => (
              <PlaceholderCard key={index} index={index} />
            ))}
      </HStack>
    </Box>
  );
};

export default ThumbnailDeck;
